import { ApartmentOutlined } from '@ant-design/icons';
import { Form, Input, Modal, TreeSelect, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { createErpProduct } from '../../api/product';

// Creates an ERP-only product (not in the CMS / storefront) from the Create
// Item product search. Name and subcategory are prefilled from the search.
function ProductCreateModal({ open, language, initialName = '', initialCategoryId, categoryTreeData = [], onCancel, onCreated }) {
    const [form] = Form.useForm()
    const [saving, setSaving] = useState(false)
    const categoryTreeRef = useRef(null)

    useEffect(() => {
        if (open) {
            form.setFieldsValue({ name: initialName.trim(), category_id: initialCategoryId })
        }
    }, [open])

    const handleSave = async () => {
        // Enter (form submit) can fire while the OK button is already saving
        if (saving) {
            return
        }
        let values
        try {
            values = await form.validateFields()
        }
        catch {
            return
        }
        setSaving(true)
        try {
            const product = await createErpProduct({ ...values, lang: language })
            message.success(product.existing ? 'Product already exists, selected it' : 'Product created')
            onCreated(product)
        }
        catch (e) {
            message.error(e.message)
        }
        finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            title="Add new product"
            open={open}
            onOk={handleSave}
            onCancel={onCancel}
            okText="Create & select"
            confirmLoading={saving}
            destroyOnHidden
            width={460}
        >
            <p style={{ color: '#888', marginTop: 0 }}>
                Available for ERP items only — it will not appear on the website.
            </p>
            <Form form={form} layout="vertical" preserve={false} onFinish={handleSave}>
                <Form.Item name="name" label="Product name" rules={[{ required: true, whitespace: true, min: 2, max: 200 }]}>
                    <Input autoFocus placeholder="e.g. Custom acrylic display" />
                </Form.Item>
                <Form.Item name="category_id" label="Subcategory" rules={[{ required: true, message: 'Select a subcategory' }]}>
                    <TreeSelect
                        ref={categoryTreeRef}
                        // rc-tree ships an old nested rc-virtual-list (3.14.x) that cuts off the
                        // last row; the list is short, so plain native scrolling is safer
                        virtual={false}
                        // the dropdown keeps its last scroll position; without a
                        // selection start at the top (with one, antd scrolls to it)
                        onOpenChange={(isOpen) => {
                            if (isOpen && !form.getFieldValue('category_id')) {
                                setTimeout(() => categoryTreeRef.current?.scrollTo(0))
                            }
                        }}
                        showSearch
                        treeData={categoryTreeData}
                        fieldNames={{ label: 'name', value: 'id', children: 'children' }}
                        treeNodeFilterProp="name"
                        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                        suffixIcon={<ApartmentOutlined />}
                        placeholder="Subcategory"
                    />
                </Form.Item>
                {/* lets Enter submit */}
                <button type="submit" hidden />
            </Form>
        </Modal>
    )
}

ProductCreateModal.propTypes = {
    open: PropTypes.bool.isRequired,
    language: PropTypes.string.isRequired,
    initialName: PropTypes.string,
    initialCategoryId: PropTypes.number,
    categoryTreeData: PropTypes.array,
    onCancel: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
}

export default ProductCreateModal;
